import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

import i18n from '../i18n';

/**
 * The codes the API attaches to every error it raises deliberately. Anything
 * else that reaches `code` came from Apollo Server itself
 * (`GRAPHQL_VALIDATION_FAILED`, `INTERNAL_SERVER_ERROR`), which is why `code`
 * below is a plain string rather than this union.
 */
export type ApiErrorCode =
  'BAD_USER_INPUT' | 'FORBIDDEN' | 'UNAUTHENTICATED' | 'NOT_FOUND' | 'GENERATION_FAILED';

/**
 * One API error, flattened into what a screen actually renders: a headline and
 * a message per field.
 *
 * The API answers every expected failure the same way -- `message` plus
 * `extensions.errors` keyed by dotted path, already in the language the request
 * asked for -- so unpacking it once here means no form has to know the shape of
 * a GraphQL error, and nothing has to translate one.
 */
export type ApiError = {
  message: string;
  code: string | null;
  /** `extensions.errors`, e.g. `{ 'labels.ru': 'Введите название' }`. */
  fields: Record<string, string>;
};

export function toApiError(error: ErrorLike | undefined | null): ApiError | null {
  if (!error) return null;

  if (CombinedGraphQLErrors.is(error)) {
    // Only the first is shown: the API raises one error per request, and a list
    // of headlines would be a worse answer than the headline.
    const first = error.errors[0];

    if (!first) return { message: error.message, code: null, fields: {} };

    const extensions = (first.extensions ?? {}) as {
      code?: unknown;
      errors?: unknown;
    };

    return {
      message: first.message,
      code: typeof extensions.code === 'string' ? extensions.code : null,
      fields: fieldsOf(extensions.errors),
    };
  }

  // A network error carries the browser's own wording ("Failed to fetch"),
  // which is not something to show an operator. Translated through the
  // singleton rather than a hook: this runs inside the link chain too.
  return { message: i18n.t('errors.network'), code: null, fields: {} };
}

export function hasCode(error: ErrorLike | undefined | null, code: ApiErrorCode): boolean {
  return toApiError(error)?.code === code;
}

export function isUnauthenticated(error: ErrorLike | undefined | null): boolean {
  return hasCode(error, 'UNAUTHENTICATED');
}

/** The message the whole screen shows, ignoring the per-field detail. */
export function errorMessage(error: ErrorLike | undefined | null): string | null {
  return toApiError(error)?.message ?? null;
}

/** The per-field messages a form puts under its inputs. */
export function fieldErrors(error: ErrorLike | undefined | null): Record<string, string> {
  return toApiError(error)?.fields ?? {};
}

function fieldsOf(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') return {};

  const fields: Record<string, string> = {};

  for (const [key, message] of Object.entries(value as Record<string, unknown>)) {
    if (typeof message === 'string') fields[key] = message;
  }

  return fields;
}
